import { SplitFactory } from '@splitsoftware/splitio';
import type { IClient } from '@splitsoftware/splitio/types/splitio';
import { isNil } from 'lodash';

import type { UserState } from 'types/user';
import { SPLIT_INCOGNITO_ID } from 'common/constants';
import { getTreatments } from 'utils/treatmentHelpers';
import config from 'config';

let cachedSplitClient: IClient | undefined;

export const createServerClient = (splitKey: string | undefined): IClient | undefined => {
  if (isNil(splitKey)) {
    console.warn(
      'Warning: There is no SPLITIO_AUTHORIZATION_KEY env var defined for the Split.io API Key. Split.io is not enabled.',
    );
    return undefined;
  }

  const splitFactory = SplitFactory({
    core: {
      authorizationKey: splitKey,
      IPAddressesEnabled: false,
    },
    debug: false,
    sync: {
      impressionsMode: 'OPTIMIZED',
    },
    startup: {
      eventsFirstPushWindow: 5,
      readyTimeout: 5,
      requestTimeoutBeforeReady: 5,
      retriesOnFailureBeforeReady: 2,
    },
  });

  return splitFactory.client();
};

/**
 * Resolves once the cached Split client fires SDK_READY or SDK_READY_TIMED_OUT.
 * Safe to call before the client exists — returns immediately if there is no key.
 * Intended for use in instrumentation.ts to warm the SDK before requests are served.
 */
export const waitForSplitReady = (): Promise<void> => {
  const client = getServerSplitClient();
  if (!client) return Promise.resolve();

  const isTestEnv = process.env.NODE_ENV === 'test';

  return client
    .ready()
    .then(() => {
      if (!isTestEnv) console.log('Split SDK_READY Event fired');
    })
    .catch(() => {
      if (!isTestEnv) console.error('Split SDK_READY_TIMED_OUT Event fired');
    });
};

export const getServerSplitClient = (): IClient | undefined => {
  if (cachedSplitClient) return cachedSplitClient;

  const key = process.env.SPLITIO_AUTHORIZATION_KEY || config.splitio;
  if (isNil(key)) return undefined;

  cachedSplitClient = createServerClient(key);

  return cachedSplitClient;
};

export const getServerTreatments = (
  treatments: string[],
  userState: UserState | null,
  anonymousId: string = SPLIT_INCOGNITO_ID,
) => {
  const splitClient = getServerSplitClient();
  if (!splitClient) return {};
  return getTreatments(splitClient, treatments, userState, anonymousId);
};

export default getServerSplitClient;
